import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { ApiError } from '@crave/api-client';
import { Card, Field } from '@crave/ui';
import { api } from '../api.ts';
import { FeedbackForm } from '../components/Feedback.tsx';
import type { Tracking } from '../lib/types.ts';

/**
 * The feedback link. Reached two ways:
 *  - `/feedback/h/<token>` from the text or email after pickup. It already knows the order, so the
 *    customer is never asked to go and find an order number (#63).
 *  - `/feedback` from the QR at the counter, where an order number is optional.
 * Each pizza on the order is rated on its own.
 */

export function FeedbackPage() {
  const { token } = useParams();
  return token ? <ForThisOrder token={`h.${token}`} /> : <Anyone />;
}

/** The link from the text: the order is named on the page and never typed. */
function ForThisOrder({ token }: { token: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['track', token],
    queryFn: async () => (await api.get<Tracking>(`/track/${token}`)).data,
    retry: false,
  });
  if (q.isError)
    return (
      <Card>
        <strong>We couldn’t find that order.</strong>
        <p style={{ margin: '6px 0 0', color: 'var(--ink-soft)' }}>
          {q.error instanceof ApiError ? q.error.message : 'Try the link in your text again.'}
        </p>
      </Card>
    );
  if (!q.data) return <p>Loading…</p>;
  const o = q.data;
  // One rating per line: two of the same pizza came out of the same oven, one after the other.
  const pizzas = o.items.map((i) => ({
    id: i.id,
    name: i.quantity > 1 ? `${i.name} × ${i.quantity}` : i.name,
    quantity: i.quantity,
  }));
  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 560, margin: '0 auto' }}>
      <div>
        <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
          Order {o.order_number} · picked up {o.pickup_label}
        </div>
        <h1 style={{ margin: '4px 0 0', fontSize: 30, lineHeight: 1.15 }}>How was it?</h1>
        <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)', lineHeight: 1.5 }}>
          {pizzas.length > 1
            ? 'Rate each pizza on its own. Twenty seconds, and no account.'
            : 'Twenty seconds, and no account.'}
        </p>
      </div>
      {o.feedback.submitted ? (
        <Card>
          <strong>Thanks, you already rated this order.</strong>
        </Card>
      ) : (
        <FeedbackForm
          title={pizzas.length > 1 ? 'Your pizzas' : 'Your rating'}
          pizzas={pizzas}
          onSubmit={async (body) => {
            await api.post(`/track/${token}/feedback`, body);
            void qc.invalidateQueries({ queryKey: ['track', token] });
          }}
        />
      )}
    </div>
  );
}

/** The QR at the counter: anyone, with or without an order number. */
function Anyone() {
  const [orderNumber, setOrderNumber] = useState('');
  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 560, margin: '0 auto' }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 30, lineHeight: 1.15 }}>How was your pizza?</h1>
        <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)', lineHeight: 1.5 }}>
          Twenty seconds, and it tells us what to keep and what to fix. You do not need an account.
        </p>
      </div>
      <Field
        label="Order number (optional)"
        value={orderNumber}
        onChange={(e) => setOrderNumber(e.target.value)}
        placeholder="CP-0920-07"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        hint="On your receipt. Leave it blank if you don't have it — the link in your text fills it in for you."
      />
      <FeedbackForm
        title="Your rating"
        askEmail
        emailHint="Only if you want us to get back to you about this."
        onSubmit={async (body) => {
          await api.post('/public/feedback', {
            ...body,
            ...(orderNumber.trim() ? { order_number: orderNumber.trim() } : {}),
          });
        }}
      />
    </div>
  );
}
